import { useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar, Avatar, Box, Button, Chip, Collapse, Divider, Drawer, IconButton, List, ListItemButton,
    ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography,
    useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TvIcon from '@mui/icons-material/Tv';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyIcon from '@mui/icons-material/Key';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import api, { hidratarSessaoSeNecessario } from '../services/api';
import { getNomeCompleto, isSenhaTemporaria, removeSession, temPermissao } from '../services/auth';
import { useColorMode } from '../contexts/ColorModeContext';
import { ehGrupo, localizarRota, modulosPermitidos, telasDoModulo } from '../utils/modulos';
import { EVENTO_FUNCIONALIDADES } from '../services/configuracao';
import Migalhas from './comuns/Migalhas';
import TrocaSenhaDialog from './usuarios/TrocaSenhaDialog';
import AssistWidget from './assist/AssistWidget';
import { NovidadesProvider } from '../contexts/NovidadesContext';
import AvisoNovidades from './novidades/AvisoNovidades';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

const LARGURA_MENU = 240;
const CHAVE_MENU = 'mpg.patio.menuAberto'; // preferência do menu no desktop
// Wiki de ajuda ao cliente (Wiki.js, doc-as-code em wiki/) — sobe sob demanda,
// independente do app. É lá que fica a seção de download dos aplicativos.
const URL_WIKI_AJUDA = 'https://ajuda.mpgsistemas.tec.br/';

// Componente memoizado para cada item do menu
const MenuLateralItem = ({ item, cor, ativo, aninhado, onClick }) => (
    <ListItemButton
        onClick={onClick}
        sx={{
            ml: aninhado ? 3 : 1,
            mr: 1,
            my: 0.3,
            borderRadius: 2,
            borderLeft: '3px solid',
            borderColor: ativo ? cor : 'transparent',
            bgcolor: ativo ? 'rgba(255,255,255,0.12)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }}
    >
        <ListItemIcon sx={{ color: ativo ? cor : 'rgba(255,255,255,0.6)', minWidth: 40 }}>
            {item.icone}
        </ListItemIcon>
        <ListItemText
            primary={item.titulo}
            primaryTypographyProps={{ fontWeight: ativo ? 700 : 400 }}
        />
    </ListItemButton>
);

const MainLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { mode, alternar } = useColorMode();
    // Desktop: menu fixo (persistente), preferência lembrada entre sessões.
    // Mobile/tablet: menu sobreposto (temporário), sempre inicia fechado.
    const [menuDesktop, setMenuDesktop] = useState(() => localStorage.getItem(CHAVE_MENU) !== 'false');
    const [menuMobile, setMenuMobile] = useState(false);
    const [anchorUsuario, setAnchorUsuario] = useState(null);
    const [anchorModulo, setAnchorModulo] = useState(null);
    const [trocaSenhaAberta, setTrocaSenhaAberta] = useState(false);
    const [trocaObrigatoria, setTrocaObrigatoria] = useState(isSenhaTemporaria());
    const [ambienteSupport, setAmbienteSupport] = useState('');

    // Recalculado quando as flags mudam. Dependência vazia deixaria o menu
    // congelado na montagem, e ligar uma funcionalidade no MPG Support só
    // apareceria depois de recarregar a página.
    const [versaoFlags, setVersaoFlags] = useState(0);
    useEffect(() => {
        const aoMudar = () => setVersaoFlags((v) => v + 1);
        window.addEventListener(EVENTO_FUNCIONALIDADES, aoMudar);
        return () => window.removeEventListener(EVENTO_FUNCIONALIDADES, aoMudar);
    }, []);
    // versaoFlags é dependência de propósito: modulosPermitidos() não recebe
    // argumento, lê as flags de fora, e o lint não tem como enxergar isso.
    // Sem a dependência o menu congela na montagem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const modulos = useMemo(() => modulosPermitidos(), [versaoFlags]);
    const { modulo: moduloAtivo, grupo: grupoAtivo } = useMemo(
        () => localizarRota(location.pathname),
        [location.pathname]
    );
    const nome = useMemo(() => getNomeCompleto(), []);
    const iniciais = useMemo(() =>
        nome.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?',
        [nome]
    );
    // Seletor rápido do topo: lista plana, e só com as telas que o perfil pode
    // ver — `localizarRota` varre MODULOS cru, então o módulo é reconciliado
    // contra a lista já filtrada por permissão.
    const itensModuloAtivo = useMemo(() => {
        const permitido = modulos.find((m) => m.id === moduloAtivo?.id) || modulos[0];
        return telasDoModulo(permitido);
    }, [moduloAtivo, modulos]);

    // Grupos colapsáveis: memoizado
    const [gruposAbertos, setGruposAbertos] = useState(() => (
        moduloAtivo ? { [moduloAtivo.id]: true } : {}
    ));
    // Subgrupos (ex.: Auditoria e logs) abrem sozinhos quando a tela atual está
    // dentro deles; fora disso o usuário controla.
    const [subgruposAbertos, setSubgruposAbertos] = useState(() => (
        grupoAtivo ? { [grupoAtivo.id]: true } : {}
    ));

    useEffect(() => {
        if (moduloAtivo?.titulo) {
            // Mantém o acordeão consistente: só o grupo do módulo ativo fica aberto.
            setGruposAbertos({ [moduloAtivo.id]: true });
        }
    }, [moduloAtivo?.id, moduloAtivo?.titulo]);

    useEffect(() => {
        hidratarSessaoSeNecessario();
        if (temPermissao('MPG_SUPPORT_ACESSAR') && typeof api.get === 'function') {
            api.get('/support/visao-geral').then(({ data }) => setAmbienteSupport(data.ambiente || '')).catch(() => {});
        }
    }, []);

    const grupoAtivoId = grupoAtivo?.id;
    useEffect(() => {
        if (grupoAtivoId) setSubgruposAbertos((prev) => ({ ...prev, [grupoAtivoId]: true }));
    }, [grupoAtivoId]);

    // Acordeão: abrir um grupo fecha os demais (no máximo um aberto por vez).
    const alternarGrupo = useCallback((id) => setGruposAbertos((prev) => (prev[id] ? {} : { [id]: true })), []);
    // Subgrupo é independente: abrir um não fecha outro (são poucos e rasos).
    const alternarSubgrupo = useCallback(
        (id) => setSubgruposAbertos((prev) => ({ ...prev, [id]: !prev[id] })),
        []
    );
    const alternarMenu = useCallback(() => {
        if (isDesktop) {
            setMenuDesktop((v) => {
                const novo = !v;
                localStorage.setItem(CHAVE_MENU, String(novo));
                return novo;
            });
        } else {
            setMenuMobile((v) => !v);
        }
    }, [isDesktop]);
    const fecharMenuUsuario = useCallback(() => setAnchorUsuario(null), []);
    const fecharMenuModulo = useCallback(() => setAnchorModulo(null), []);
    const irPara = useCallback((rota) => { setAnchorModulo(null); navigate(rota); }, [navigate]);
    const fecharTrocaSenha = useCallback(() => { setTrocaSenhaAberta(false); setTrocaObrigatoria(false); }, []);

    const sair = useCallback(async () => {
        try { await api.post('/auth/logout'); } catch { /* logout local mesmo se falhar */ }
        removeSession();
        navigate('/login', { replace: true });
    }, [navigate]);

    const handleItemClick = useCallback((rota) => {
        setMenuMobile(false); // fecha o menu sobreposto (mobile) ao navegar; no desktop permanece fixo
        navigate(rota);
    }, [navigate]);

    const renderItem = useCallback((item, cor, aninhado = false) => {
        const ativo = location.pathname === item.rota
            || (item.rota !== '/support' && location.pathname.startsWith(`${item.rota}/`));
        return (
            <MenuLateralItem
                key={item.rota}
                item={item}
                cor={cor}
                ativo={ativo}
                aninhado={aninhado}
                onClick={() => handleItemClick(item.rota)}
            />
        );
    }, [location.pathname, handleItemClick]);

    // Uma entrada do módulo: tela direta ou subgrupo colapsável de telas.
    const renderEntrada = useCallback((entrada, cor) => {
        if (!ehGrupo(entrada)) return renderItem(entrada, cor);
        const aberto = !!subgruposAbertos[entrada.id];
        return (
            <Box key={entrada.id}>
                <ListItemButton
                    onClick={() => alternarSubgrupo(entrada.id)}
                    sx={{ ml: 1, mr: 1, my: 0.3, borderRadius: 2 }}
                >
                    <ListItemIcon sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 40 }}>
                        {entrada.icone}
                    </ListItemIcon>
                    <ListItemText primary={entrada.titulo} />
                    {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </ListItemButton>
                <Collapse in={aberto} timeout="auto" unmountOnExit>
                    {entrada.itens.map((sub) => renderItem(sub, cor, true))}
                </Collapse>
            </Box>
        );
    }, [renderItem, subgruposAbertos, alternarSubgrupo]);

    // Lista de módulos/itens: mesma no menu fixo (desktop) e no sobreposto (mobile).
    const listaMenu = (
        <>
            <Toolbar />
            <List>
                {modulos.map((modulo) => (
                    modulo.titulo ? (
                        // Módulo com cabeçalho: grupo colapsável (dropdown)
                        <Box key={modulo.id}>
                            <ListItemButton
                                onClick={() => alternarGrupo(modulo.id)}
                                sx={{ mx: 1, borderRadius: 2, mt: 0.5 }}
                            >
                                <ListItemIcon sx={{ color: modulo.cor, minWidth: 40 }}>
                                    {modulo.icone}
                                </ListItemIcon>
                                <ListItemText
                                    primary={modulo.titulo}
                                    primaryTypographyProps={{
                                        variant: 'overline',
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                    }}
                                />
                                {gruposAbertos[modulo.id] ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={gruposAbertos[modulo.id]} timeout="auto" unmountOnExit>
                                {modulo.itens.map((item) => renderEntrada(item, modulo.cor))}
                            </Collapse>
                        </Box>
                    ) : (
                        // Início (Dashboard): sempre visível, sem cabeçalho
                        <Box key={modulo.id}>
                            {modulo.itens.map((item) => renderItem(item, modulo.cor))}
                        </Box>
                    )
                ))}
            </List>
        </>
    );

    const estiloPaper = {
        width: LARGURA_MENU,
        boxSizing: 'border-box',
        bgcolor: 'secondary.main',
        color: '#fff',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
    };

    const conteudoDrawer = isDesktop ? (
        // Desktop: menu fixo que empurra o conteúdo; recolhível pelo botão do topo.
        menuDesktop && (
            <Drawer
                variant="permanent"
                sx={{ width: LARGURA_MENU, flexShrink: 0, '& .MuiDrawer-paper': estiloPaper }}
            >
                {listaMenu}
            </Drawer>
        )
    ) : (
        // Mobile/tablet: menu sobreposto, fecha ao clicar fora ou navegar.
        <Drawer
            variant="temporary"
            open={menuMobile}
            onClose={() => setMenuMobile(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': estiloPaper }}
        >
            {listaMenu}
        </Drawer>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderRadius: 0 }}>
                <Toolbar>
                    {temPermissao('MPG_SUPPORT_ACESSAR') && <Chip
                        label={`Modo Suporte MPG${ambienteSupport.toLowerCase().includes('prod') ? ' — PRODUÇÃO' : ''}`}
                        color={ambienteSupport.toLowerCase().includes('prod') ? 'error' : 'warning'}
                        sx={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 2, fontWeight: 800 }}
                    />}
                    <IconButton color="inherit" edge="start" onClick={alternarMenu} sx={{ mr: 2 }} aria-label="Abrir ou fechar o menu">
                        <MenuIcon />
                    </IconButton>
                    <LocalShippingIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ mr: 2 }}>
                        MPG Pátio
                    </Typography>

                    {/* Seletor rápido do módulo atual: pula entre telas do mesmo módulo */}
                    {moduloAtivo && (
                        <>
                            <Button
                                color="inherit"
                                onClick={(e) => setAnchorModulo(e.currentTarget)}
                                startIcon={moduloAtivo.icone}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                    px: 1.5,
                                }}
                            >
                                {moduloAtivo.rotulo}
                            </Button>
                            <Menu
                                anchorEl={anchorModulo}
                                open={!!anchorModulo}
                                onClose={fecharMenuModulo}
                            >
                                {itensModuloAtivo.map((item) => (
                                    <MenuItem key={item.rota} onClick={() => irPara(item.rota)}>
                                        <ListItemIcon sx={{ color: moduloAtivo.cor }}>{item.icone}</ListItemIcon>
                                        {item.titulo}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    <Tooltip title="Painel de chamada (TV)">
                        <IconButton color="inherit" onClick={() => window.open('/painel', '_blank')}>
                            <TvIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Ajuda">
                        {/* noopener,noreferrer: destino é externo, ao contrário do /painel acima. */}
                        <IconButton
                            color="inherit"
                            onClick={() => window.open(URL_WIKI_AJUDA, '_blank', 'noopener,noreferrer')}
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={mode === 'light' ? 'Modo escuro' : 'Modo claro'}>
                        <IconButton color="inherit" onClick={alternar}>
                            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={nome}>
                        <IconButton onClick={(e) => setAnchorUsuario(e.currentTarget)} sx={{ ml: 1 }}>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: 14 }}>
                                {iniciais}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorUsuario}
                        open={!!anchorUsuario}
                        onClose={fecharMenuUsuario}
                    >
                        <MenuItem disabled>{nome}</MenuItem>
                        <MenuItem onClick={() => { fecharMenuUsuario(); navigate('/novidades'); }}>
                            <ListItemIcon><NewReleasesIcon fontSize="small" /></ListItemIcon>
                            Novidades do sistema
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { fecharMenuUsuario(); setTrocaSenhaAberta(true); }}>
                            <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
                            Trocar senha
                        </MenuItem>
                        <MenuItem onClick={sair}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            Sair
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {conteudoDrawer}

            <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
                <Toolbar />
                <Migalhas />
                {!trocaObrigatoria && <AvisoNovidades />}
                <Outlet />
            </Box>

            <TrocaSenhaDialog
                aberta={trocaSenhaAberta || trocaObrigatoria}
                obrigatoria={trocaObrigatoria}
                onFechar={fecharTrocaSenha}
            />

            <AssistWidget />
        </Box>
    );
};

export default function MainLayoutComNovidades() {
    return <NovidadesProvider><MainLayout /></NovidadesProvider>;
}
